import PropTypes from 'prop-types';
import './Table.css';

// Data-table helpers — the small pieces the screens assemble around a
// raw `<table className="tbl">`. Styles live in atoms.css (.tbl, .th-sort,
// .skel, .tbl-empty). Mirrors the prototype's Th / SortHead / SkeletonRows /
// EmptyRow (lists.jsx). The screen owns the <table>; these own the cells.

// Plain header cell with an optional disambiguating tooltip.
export function Th({ children, tip, w, num }) {
  return (
    <th
      style={w ? { width: w } : undefined}
      className={num ? 'num' : undefined}
    >
      {tip ? (
        <span className="tip" data-tip={tip}>
          {children}
        </span>
      ) : (
        children
      )}
    </th>
  );
}

Th.propTypes = {
  children: PropTypes.node,
  tip: PropTypes.string,
  w: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  num: PropTypes.bool,
};

// Sortable header cell. Click cycles asc → desc on its column; clicking a new
// column starts at asc. Wired to the server sort via (sortKey, sortBy, onSort).
// The th carries aria-sort; the control is a real button inside it, so the
// header is reachable and operable from the keyboard (Tab, Enter, Space).
export function SortHead({ label, k, sortKey, sortBy, onSort, w, num, tip }) {
  const active = sortKey === k;
  const dir = active ? sortBy : null;
  const handle = () =>
    onSort(k, active && dir === 'asc' ? 'desc' : active ? 'asc' : 'asc');
  return (
    <th
      style={w ? { width: w } : undefined}
      className={`th-sort${num ? ' num' : ''}${active ? ' is-on' : ''}`}
      aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button type="button" className="th-sort__in" onClick={handle}>
        {tip ? (
          <span className="tip" data-tip={tip}>
            {label}
          </span>
        ) : (
          label
        )}
        <span className="th-sort__caret" aria-hidden="true">
          {active ? (dir === 'asc' ? '↑' : '↓') : '↕'}
        </span>
      </button>
    </th>
  );
}

SortHead.propTypes = {
  label: PropTypes.node.isRequired,
  k: PropTypes.string.isRequired,
  sortKey: PropTypes.string,
  sortBy: PropTypes.oneOf(['asc', 'desc']),
  onSort: PropTypes.func.isRequired,
  w: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  num: PropTypes.bool,
  tip: PropTypes.string,
};

// Loading skeleton rows — staggered bar widths so the table reads as "loading"
// without nagging motion.
export function SkeletonRows({ rows = 5, cols = 4 }) {
  return Array.from({ length: rows }).map((_, i) => (
    <tr key={i} className="skel-row">
      {Array.from({ length: cols }).map((__, j) => (
        <td key={j}>
          <span
            className="skel"
            style={{ width: j === 0 ? '60%' : `${40 + ((j * 13) % 45)}%` }}
          />
        </td>
      ))}
    </tr>
  ));
}

SkeletonRows.propTypes = {
  rows: PropTypes.number,
  cols: PropTypes.number,
};

// Full-width empty state row inside a table body.
export function EmptyRow({ span, title, hint }) {
  return (
    <tr className="tbl__empty-row">
      <td colSpan={span}>
        <div className="tbl-empty">
          <div className="tbl-empty__t">{title}</div>
          {hint && <div className="note">{hint}</div>}
        </div>
      </td>
    </tr>
  );
}

EmptyRow.propTypes = {
  span: PropTypes.number.isRequired,
  title: PropTypes.node,
  hint: PropTypes.node,
};
